import type { GroupBy, GroupProperty } from '../../core/common/types.js';
import type { FilterGroup } from '../../core/filter/types.js';
import type { Sort } from '../../core/sort/types.js';
import { type BasicViewDataType, viewType } from '../../core/view/data-view.js';
import { GanttSingleView } from './gantt-view-manager.js';

export const ganttViewType = viewType('gantt');

export type GanttViewColumn = {
  id: string;
  hide?: boolean;
  /** Per-property width of the extra label column, in px. Unset = default. */
  width?: number;
};

export type GanttZoomLevel = 'day' | 'week' | 'month' | 'quarter';
export type GanttSnapMode = 'day' | 'week' | 'none';

type DataType = {
  columns: GanttViewColumn[];
  filter: FilterGroup;
  groupBy?: GroupBy;
  groupProperties?: GroupProperty[];
  sort?: Sort;
  header?: {
    titleColumn?: string;
    iconColumn?: string;
  };
  /** Property id of the start-date column (set by setup wizard). */
  startDateCol?: string;
  /** Property id of the end-date column (set by setup wizard). */
  endDateCol?: string;
  /** Optional progress-property id used for the in-bar progress fill. */
  progressCol?: string;
  /** Optional select-property id used to colour bars by status. */
  statusCol?: string;
  /** Optional member-property id used to show the task assignee. */
  assigneeCol?: string;
  /** Property id of an optional Relation column used for dependencies. */
  dependenciesCol?: string;
  allDayCol?: string;
  timezoneCol?: string;
  recurrenceCol?: string;
  recurrenceExdateCol?: string;
  recurrenceIdCol?: string;
  calendarIdCol?: string;
  locationPhysicalCol?: string;
  locationVirtualCol?: string;
  descriptionCol?: string;
  attendeesCol?: string;
  reminderCol?: string;
  visibilityCol?: string;
  zoomLevel?: GanttZoomLevel;
  snapMode?: GanttSnapMode;
  /**
   * Title-column width of the sticky label section, in px. Shared
   * via Y.Doc to match the per-extra-column widths (and the Table
   * view's column widths) so all layout geometry of the view lives
   * in one place. Missing ⇒ fall back to
   * {@link DEFAULT_LABEL_COLUMN_WIDTH}.
   */
  labelColumnWidth?: number;
  /**
   * User-dragged maximum width of the whole sticky label section, in
   * px. `undefined` ⇒ no clamp (section grows with its content).
   * Persisted on the view so "I made the label narrower" carries
   * across reloads and devices, in sync with the extras' widths.
   */
  labelSectionMaxWidth?: number;
  /**
   * Ordered list of gantt-specific roles shown on each bar. The role
   * maps to the column id configured elsewhere on the view
   * (`titleColumn`, `assigneeCol`, `startDateCol`, `endDateCol`,
   * `statusCol`, `progressCol`), so bar-items never drift from the
   * wizard-picked columns. Title is the only mandatory role; it
   * auto-seeds into the array when absent so rendering stays stable
   * for freshly-created views.
   */
  barDisplayItems?: GanttBarDisplayItem[];
};

/**
 * Kind of bar display chip. Every value maps to exactly one of the
 * view-level role columns, so the renderer doesn't need an arbitrary
 * property picker — users pick from a fixed set that mirrors the
 * setup wizard's roles.
 */
export type GanttBarDisplayKind =
  | 'title'
  | 'assignee'
  | 'start'
  | 'end'
  | 'status'
  | 'progress';

/**
 * Assignee render variants. Applies only to `kind: 'assignee'` items.
 * `both` is the default because it carries the most information; the
 * narrower variants exist for users who want the bar to stay compact
 * on small displays.
 */
export type GanttAssigneeVariant = 'avatar' | 'name' | 'both';

export type GanttBarDisplayItem = {
  kind: GanttBarDisplayKind;
  visible: boolean;
  /** Only honored when `kind === 'assignee'`; ignored otherwise. */
  variant?: GanttAssigneeVariant;
};

export type GanttViewData = BasicViewDataType<
  typeof ganttViewType.type,
  DataType
>;

export const ganttViewModel = ganttViewType.createModel<GanttViewData>({
  defaultName: 'Gantt View',
  dataViewManager: GanttSingleView,
  defaultData: viewManager => {
    const columns: string[] =
      viewManager.dataSource.getBoundedPropertyIdsWindow().propertyIds;
    return {
      columns: columns.map((id: string) => ({ id })),
      filter: {
        type: 'group',
        op: 'and',
        conditions: [],
      },
      header: {
        titleColumn: columns.find(
          (id: string) => viewManager.dataSource.propertyTypeGet(id) === 'title'
        ),
      },
      zoomLevel: 'week',
      snapMode: 'day',
    };
  },
});
